// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { canSeeInvoiceHistoryLink } from "@sc/App/Store/Selectors/GeneralSelectors";
import { XNGAGE_CUSTOM_WIDGET } from "@sc/XDL/constants";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    link = "link",
    title = "title",
    openInNewWindow = "openInNewWindow",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.link]: string;
        [fields.title]: string;
        [fields.openInNewWindow]: boolean;
    };
}

interface InvoiceHistoryLinkStyles {
    link?: LinkPresentationProps;
    button?: ButtonPresentationProps;
}

export const invoiceHistoryLinkStyles: InvoiceHistoryLinkStyles = {};

const styles = invoiceHistoryLinkStyles;
const InvoiceHistoryLink: FC<OwnProps> = ({ fields: { title, link, openInNewWindow } }) => {
    const { showInvoiceHistoryLink } = useAppSelector(state => ({
        showInvoiceHistoryLink: canSeeInvoiceHistoryLink(state),
    }));

    if (!showInvoiceHistoryLink) {
        return null;
    }

    return (
        <Link
            href={link}
            {...styles.link}
            data-test-selector="invoice-history-link"
            aria-label={title}
            target={openInNewWindow ? "_blank" : ""}
        >
            {title}
        </Link>
    );
};

const widgetModule: WidgetModule = {
    component: InvoiceHistoryLink,
    definition: {
        group: XNGAGE_CUSTOM_WIDGET,
        displayName: "Invoice History Link",
        fieldDefinitions: [
            {
                name: fields.link,
                displayName: "Link",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Invoice History",
                fieldType: "General",
            },
            {
                name: fields.openInNewWindow,
                displayName: "Open in New Window",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
